import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/features/auth/AuthProvider'
import { createCheckInSyncEngine } from '@/features/check-in/checkInSync'
import { checkInStore as defaultCheckInStore, type CheckInStore } from '@/features/check-in/checkInStore'
import { clearMyContacts } from './myContactsCache'
import { outbox as defaultOutbox, type Outbox } from './outbox'
import { createSyncEngine, type SyncEngine } from './syncEngine'

export type SyncContextValue = Readonly<{
  /** Registrations on this device not yet accepted by the server. */
  waiting: number
  needsAttention: number
  /** Check-ins made on this device not yet confirmed by the server. */
  checkInsWaiting: number
  checkInsNeedingAttention: number
  syncing: boolean
  /** Bumps whenever the queue changes, so lists can re-read it. */
  changes: number
  /** Re-read the queue and send what is due (after a save, or on Sync now). */
  syncNow: () => Promise<void>
}>

const noop: SyncContextValue = { waiting: 0, needsAttention: 0, checkInsWaiting: 0, checkInsNeedingAttention: 0, syncing: false, changes: 0, syncNow: () => Promise.resolve() }
/** Exported for tests that need to observe sync requests. */
export const SyncContext = createContext<SyncContextValue>(noop)

/** How often a waiting queue is retried while the app is open and online. */
export const syncInterval = 15_000

/**
 * Runs registration and check-in sync for the signed-in user. The browser's Background
 * Sync is not relied on: sync runs when the app opens, on reconnect, when the
 * app returns to the foreground, on a timer while anything is waiting, and
 * whenever a screen asks (a save, or Sync now).
 */
export function SyncProvider({ children, queue = defaultOutbox, engine, checkIns = defaultCheckInStore, checkInEngine }: { children: ReactNode; queue?: Outbox; engine?: SyncEngine; checkIns?: CheckInStore; checkInEngine?: SyncEngine }) {
  const { session } = useAuth()
  const userId = session?.user.id ?? null
  const syncEngine = useMemo(() => engine ?? createSyncEngine({ queue }), [engine, queue])
  const checkInSync = useMemo(() => checkInEngine ?? createCheckInSyncEngine({ store: checkIns }), [checkInEngine, checkIns])
  const [counts, setCounts] = useState({ waiting: 0, needsAttention: 0, checkInsWaiting: 0, checkInsNeedingAttention: 0 })
  const [syncing, setSyncing] = useState(false)
  const [changes, setChanges] = useState(0)
  // Async work checks this before writing state, so a sign-out mid-sync
  // never shows the previous user's counts.
  const userRef = useRef(userId)
  useEffect(() => { userRef.current = userId }, [userId])

  // The copy of All Mine is the signed-out person's contacts: drop it with
  // their session. (Registrations still waiting stay: they must still sync.)
  const lastUserId = useRef(userId)
  useEffect(() => {
    if (userId) { lastUserId.current = userId; return }
    const previous = lastUserId.current
    lastUserId.current = null
    if (previous) {
      void clearMyContacts(previous).catch(() => undefined)
      void checkIns.clearDatasets(previous).catch(() => undefined)
    }
  }, [userId, checkIns])

  const recount = useCallback(async (forUser: string) => {
    const [registrations, checkInCounts] = await Promise.all([queue.counts(forUser), checkIns.counts(forUser)])
    if (userRef.current !== forUser) return
    setCounts({ ...registrations, checkInsWaiting: checkInCounts.waiting, checkInsNeedingAttention: checkInCounts.needsAttention })
    setChanges((value) => value + 1)
  }, [queue, checkIns])

  const syncNow = useCallback(async () => {
    const forUser = userRef.current
    if (!forUser) return
    await recount(forUser)
    if (!navigator.onLine) return
    setSyncing(true)
    try {
      await Promise.allSettled([syncEngine.sync(forUser), checkInSync.sync(forUser)])
    } catch {
      // The queues keep everything; the next trigger tries again.
    } finally {
      if (userRef.current === forUser) setSyncing(false)
      await recount(forUser)
    }
  }, [recount, syncEngine, checkInSync])

  // Opening the app (or signing in): recover anything a closed tab left in flight, then send.
  useEffect(() => {
    if (!userId) return
    void Promise.all([queue.recoverInterrupted(userId).catch(() => 0), checkIns.recoverInterrupted(userId).catch(() => 0)]).then(() => syncNow())
  }, [queue, checkIns, syncNow, userId])

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') void syncNow() }
    const onOnline = () => void syncNow()
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [syncNow])

  useEffect(() => {
    if (!userId || counts.waiting + counts.checkInsWaiting === 0) return
    const timer = window.setInterval(() => void syncNow(), syncInterval)
    return () => window.clearInterval(timer)
  }, [counts.waiting, counts.checkInsWaiting, syncNow, userId])

  const value = useMemo(() => (userId ? { ...counts, syncing, changes, syncNow } : noop), [userId, counts, syncing, changes, syncNow])
  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>
}

export function useSync(): SyncContextValue {
  return useContext(SyncContext)
}
